import { useState } from 'react'
import { Check, X, UserX, CalendarClock, AlertTriangle } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Textarea } from '../../lib/shadcn/textarea'
import { Label } from '../../lib/shadcn/label'
import { Spinner } from '../../lib/shadcn/spinner'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../lib/shadcn/dialog'
import { roundLabel } from '../../utils/interviews'

export type OutcomeAction = 'passed' | 'failed' | 'no_show' | 'reschedule'

const ACTIONS: Record<
  OutcomeAction,
  {
    label: string
    verb: string
    icon: typeof Check
    variant: 'default' | 'destructive' | 'outline' | 'secondary'
  }
> = {
  passed: { label: 'Mark Passed', verb: 'passed', icon: Check, variant: 'default' },
  failed: { label: 'Mark Failed', verb: 'failed', icon: X, variant: 'destructive' },
  no_show: { label: 'Mark No-show', verb: 'a no-show', icon: UserX, variant: 'outline' },
  reschedule: { label: 'Reschedule', verb: 'for reschedule', icon: CalendarClock, variant: 'secondary' },
}

// Passed and Failed are not offered here. Since migration 0265 the outcome of an
// interview comes from its scorecard (InterviewScoringPanel), so a button that
// chose it would put back exactly the discretion the rubric removed. No-show and
// Reschedule stay: they are facts about the booking, not a judgement of the man.
const ORDER: OutcomeAction[] = ['no_show', 'reschedule']

export function DecisionActions({
  interviewId,
  applicantName,
  round,
  canAct,
  busy,
  onSubmit,
}: {
  interviewId: string
  applicantName: string
  round: number
  canAct: boolean
  busy: boolean
  onSubmit: (interviewId: string, action: OutcomeAction, notes: string | null) => Promise<void>
}) {
  const [notes, setNotes] = useState('')
  const [action, setAction] = useState<OutcomeAction | null>(null)

  const handleConfirm = async () => {
    if (!action) return
    const trimmed = notes.trim()
    await onSubmit(interviewId, action, trimmed.length ? trimmed : null)
    setAction(null)
    setNotes('')
  }

  const activeAction = action ? ACTIONS[action] : null
  const isReschedule = action === 'reschedule'
  const notesId = `notes-${interviewId}`

  if (!canAct) return null

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label htmlFor={notesId} className="text-xs font-medium text-muted-foreground">
          Notes on a no-show or a reschedule
        </Label>
        <Textarea
          id={notesId}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="What happened with the booking"
          disabled={busy}
          rows={3}
        />
      </div>

      <div className="grid grid-cols-1 gap-2 sm:flex sm:flex-wrap">
        {ORDER.map((a) => {
          const cfg = ACTIONS[a]
          const Icon = cfg.icon
          return (
            <Button
              key={a}
              variant={cfg.variant}
              disabled={busy}
              onClick={() => setAction(a)}
              className="h-11 w-full justify-center sm:h-9 sm:w-auto"
            >
              <Icon className="h-4 w-4" />
              {cfg.label}
            </Button>
          )
        })}
      </div>

      <Dialog open={action !== null} onOpenChange={(open) => !open && !busy && setAction(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm decision</DialogTitle>
            <DialogDescription>
              Record {roundLabel(round).toLowerCase()} for{' '}
              <span className="font-medium text-foreground">{applicantName}</span> as{' '}
              <span className="font-medium text-foreground">{activeAction?.verb}</span>.
            </DialogDescription>
          </DialogHeader>

          {isReschedule && (
            <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>This cancels the current slot and emails the driver to rebook.</span>
            </div>
          )}

          {notes.trim() ? (
            <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
              <span className="font-medium text-foreground">Notes: </span>
              {notes.trim()}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">No notes will be saved with this decision.</p>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAction(null)}
              disabled={busy}
              className="h-11 w-full sm:h-9 sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              variant={activeAction?.variant ?? 'default'}
              onClick={handleConfirm}
              disabled={busy}
              className="h-11 w-full sm:h-9 sm:w-auto"
            >
              {busy && <Spinner className="h-4 w-4" />}
              {activeAction ? activeAction.label : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
